"use client";

import { motion, AnimatePresence } from "framer-motion";
import { Play, Info } from "lucide-react";
import Image from "next/image";
import { useState, useEffect } from "react";
import type { YouTubePlaylistItem } from "@/lib/youtube";

const blurDataURL =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyIiBoZWlnaHQ9IjgiIGZpbGw9IiNlZmVmZWYiLz48L3N2Zz4=";

interface FeaturedHeroProps {
  videos: YouTubePlaylistItem[];
  onPlayVideo: (videoId: string, title: string) => void;
}

export const FeaturedHero = ({ videos, onPlayVideo }: FeaturedHeroProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const featuredVideo = videos[currentIndex];

  // Auto-rotate the featured video every 6 seconds, but let the page settle
  // first: rotating during initial load kept repainting the hero, dragging
  // Speed Index from ~2s to 8s, and competes with above-the-fold downloads.
  // Skipped entirely for users who prefer reduced motion.
  useEffect(() => {
    if (videos.length <= 1) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let interval: ReturnType<typeof setInterval> | undefined;
    const startDelay = setTimeout(() => {
      interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % Math.min(videos.length, 5));
      }, 6000);
    }, 12000);

    return () => {
      clearTimeout(startDelay);
      if (interval) clearInterval(interval);
    };
  }, [videos.length]);

  if (!featuredVideo) return null;

  // Use maxres thumbnail URL for best quality
  const highResThumbnail = featuredVideo.thumbnail?.includes("hqdefault")
    ? featuredVideo.thumbnail.replace("hqdefault", "maxresdefault")
    : featuredVideo.thumbnail;

  return (
    <section className="relative h-[85vh] md:h-[90vh] overflow-hidden">
      {/* Background Image with crossfade. initial={false} keeps the slide
          crossfade but skips the mount fade: opacity-gating the LCP hero
          image hid it until hydration on throttled devices */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          <Image
            src={highResThumbnail}
            alt={featuredVideo.title}
            fill
            sizes="100vw"
            className="object-cover"
            preload
            quality={75}
            placeholder="blur"
            blurDataURL={blurDataURL}
          />
        </motion.div>
      </AnimatePresence>

      {/* Gradients - Light mode uses white, dark mode uses black */}
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-100 via-zinc-100/50 dark:from-brand-black dark:via-brand-black/50 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-100/90 via-zinc-100/40 dark:from-brand-black/90 dark:via-brand-black/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-zinc-100 dark:from-brand-black to-transparent" />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-32 md:pb-40 px-6 md:px-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            {/* Badge */}
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded bg-brand-red text-white text-[10px] font-bold uppercase tracking-widest">
                Featured
              </span>
              <span className="text-zinc-500 dark:text-zinc-400 text-sm font-medium">
                Youth Alive Fellowship Canada
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-black text-zinc-900 dark:text-white leading-[0.95] mb-6 tracking-tight drop-shadow-lg">
              {featuredVideo.title}
            </h1>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => onPlayVideo(featuredVideo.videoId, featuredVideo.title)}
                className="flex items-center gap-3 bg-zinc-900 dark:bg-white text-white dark:text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-zinc-800 dark:hover:bg-white/90 transition-colors shadow-xl"
              >
                <Play className="w-6 h-6 fill-current" />
                Play
              </button>

              <button
                onClick={() => onPlayVideo(featuredVideo.videoId, featuredVideo.title)}
                className="flex items-center gap-3 bg-black/10 dark:bg-white/20 backdrop-blur-sm text-zinc-900 dark:text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-black/20 dark:hover:bg-white/30 transition-colors border border-black/10 dark:border-white/10"
              >
                <Info className="w-6 h-6" />
                More Info
              </button>
            </div>
          </motion.div>
        </AnimatePresence>

      </div>
    </section>
  );
};
